import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth';
import logoUrl from '../assets/cpe-logo.png';

type Mode = 'phone' | 'email';

export function LoginPage() {
  const { me, loading, loginEmail, loginPhone } = useAuth();
  const [mode, setMode] = useState<Mode>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) return <FullPage>Cargando…</FullPage>;
  if (me) return <Navigate to="/" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'phone') await loginPhone(phone);
      else await loginEmail(email, password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      setError(translateError(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FullPage>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-7 sm:p-9 space-y-7">
        <div className="flex flex-col items-center gap-3">
          <img
            src={logoUrl}
            alt="CPE"
            className="h-20 w-auto object-contain"
          />
          <h1 className="text-3xl font-bold text-brand-700">Créditos</h1>
          <p className="text-base text-slate-500 text-center">Reclamos de mercancía dañada</p>
        </div>

        <div className="grid grid-cols-2 gap-1.5 bg-slate-100 rounded-xl p-1.5">
          <button
            type="button"
            className={`py-3 rounded-lg text-base font-semibold transition ${mode === 'phone' ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
            onClick={() => setMode('phone')}
          >
            Encargado
          </button>
          <button
            type="button"
            className={`py-3 rounded-lg text-base font-semibold transition ${mode === 'email' ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
            onClick={() => setMode('email')}
          >
            CPE
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          {mode === 'phone' ? (
            <Field label="Número de teléfono">
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(787) 555-1234"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="w-full px-5 py-4 text-2xl rounded-xl border-2 border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
              />
            </Field>
          ) : (
            <>
              <Field label="Email">
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-5 py-4 text-lg rounded-xl border-2 border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                />
              </Field>
              <Field label="Contraseña">
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-5 pr-14 py-4 text-lg rounded-xl border-2 border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-slate-500 hover:text-slate-800 active:text-slate-900 rounded-lg hover:bg-slate-100"
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                        <line x1="3" y1="3" x2="21" y2="21" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </Field>
            </>
          )}

          {error && (
            <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-4 font-medium">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-xl font-bold rounded-xl shadow-md transition"
          >
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        <p className="text-sm text-slate-400 text-center px-2">
          {mode === 'phone'
            ? 'Solo necesitas tu teléfono. Si no entra, contacta al admin.'
            : 'Cuenta de CPE (Customer Service / Admin)'}
        </p>
      </div>
    </FullPage>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-base font-semibold text-slate-700 mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-brand-50 to-slate-100 flex items-center justify-center p-4">
      {children}
    </div>
  );
}

function translateError(msg: string): string {
  if (/credentials/i.test(msg)) return 'Email o contraseña incorrectos.';
  if (/not registered/i.test(msg)) return 'Tu teléfono no está registrado. Contacta al admin.';
  if (/invalid phone/i.test(msg)) return 'Número de teléfono inválido.';
  return msg;
}
